import React, { Dispatch, SetStateAction, useState, useRef, useCallback } from "react";
import { Grid, IconButton, Box, Typography, Button } from "@material-ui/core";
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from "@material-ui/icons";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import OutlinedDiv from "components/haven-design/container/outlined";
import useSnackbar from "components/haven-design/snackbar/use-snackbar";

const FILE_SIZE_LIMIT = 20 * 1024 * 1024; // 20 MB

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `2px dashed ${theme.palette.grey[400]}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(4),
      textAlign: "center",
      cursor: "pointer",
      minHeight: 200,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      transition: "background-color 0.2s ease",
    },
    dragActive: {
      backgroundColor: theme.palette.action.hover,
    },
    icon: {
      fontSize: 48,
      marginBottom: theme.spacing(2),
      color: theme.palette.grey[500],
    },
  }));

interface FileUploaderProps {
  onFilesSelected?: (files: File[]) => void;
}

const DNDUploadSelector: React.FC<FileUploaderProps> = ({ onFilesSelected }) => {
  const { openSnackbar } = useSnackbar();
  const classes = useStyles();
  const [isDragActive, setIsDragActive] = useState(false);
  // Ref to the container element
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleBrowseClick = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  const validateFile = (file: File): boolean => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      openSnackbar({ message: "Only CSV files are allowed.", status: "error", duration: 5000 });
      return false;
    }
    if (file.size > FILE_SIZE_LIMIT) {
      openSnackbar({ message: "File size exceeds the 20 MB limit.", status: "error", duration: 5000 });
      return false;
    }
    return true;
  };

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!validateFile(file)) {
        event.target.value = "";
        return;
      }
      if (onFilesSelected) onFilesSelected([file]);
    },
    [onFilesSelected],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // If the element we're moving into is still a child of the container, don't change state.
    if (
      containerRef.current &&
      event.relatedTarget &&
      containerRef.current.contains(event.relatedTarget as Node)
    ) {
      return;
    }
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    const droppedFiles = event.dataTransfer.files;
    if (!droppedFiles.length) return;

    const file = droppedFiles[0];
    if (!validateFile(file)) return;
    if (onFilesSelected) {
      onFilesSelected([file]);
    }
  };

  return (
    <div ref={containerRef}>
      <Box
        data-cy="dnd-upload"
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`${classes.container} ${isDragActive ? classes.dragActive : ""}`}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept=".csv"
        />
        <CloudUploadIcon className={classes.icon} />
        <Typography variant="h6">
          {isDragActive ? "Drop the CSV file here..." : "Drag and drop your CSV file"}
        </Typography>
        <Typography variant="body1">or</Typography>
        <Button variant="contained" color="primary" style={{ marginTop: 8 }} onClick={handleBrowseClick}>
          Select a file
        </Button>
        <Typography variant="body2" color="textSecondary">
          Max file size: 20 MB
        </Typography>
      </Box>
    </div>
  );
};

type DNDUploadProps = {
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
};


const getFileSizeHumanReadable = (file: File) => {
  const bytes = file.size;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`;
};

const DNDUpload: React.FC<DNDUploadProps> = ({
  files,
  setFiles,
}) => {

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <Grid item>
      {files.length === 0 ? (<DNDUploadSelector onFilesSelected={setFiles} />) : (
        <OutlinedDiv style={{ padding: "1rem", paddingTop: "1rem", marginLeft: "0" }}>
          {files.map((file, index) => (
            <Grid key={index} item xs={12} container>
              <Grid item xs={10}>
                <Typography variant="h6">{file.name}</Typography>
                <Typography variant="body2" style={{ wordBreak: "break-word" }}>
                  {getFileSizeHumanReadable(file)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton data-cy="remove-file" onClick={() => handleRemoveFile(index)} style={{ float: "right" }}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </OutlinedDiv>
      )}
    </Grid>
  );
};

export default DNDUpload;
